import amplitudeService from '@features/analytics/amplitudeService';
import PricingCard from './PricingCard';
import {freeFeatures, plusFeatures, proFeatures} from './planFeatureList';
import type {PlanFeature} from './planFeatureList';
import {trpc} from '@features/utils/trpc';
import {cn} from '@features/ui/utils';
import {useCheckoutSession} from './useCheckoutSession';
import {AnnualUpgradeConfirmDialog} from '@features/ui/components/AnnualUpgradeConfirmDialog';
import {TierUpgradeConfirmDialog} from '@features/ui/components/TierUpgradeConfirmDialog';
import {useState} from 'react';
import {useNavigate} from '@tanstack/react-router';
import type {PlanType} from './PlanTypeSelector';
import {captureException} from '@sentry/react';
import type {BillingInterval} from './subscriptionService';
import type {SubscriptionTier} from './subscriptionTier';
import {FreeTeamCard, TeamPlusCard, TeamProCard} from './TeamPricingCards';
import {
  pricing,
  discountPercent,
  yearlyMonthlyEquivalent,
  formatPriceLabel,
} from './pricing';
import {useSubscription} from './subscriptionHook';

interface Props {
  className?: string;
  planType: PlanType;
  align?: 'left' | 'center';
  /** Hide the free plan card when a paid plan is active (used in Settings page) */
  hideFreePlan?: boolean;
}

export function SubscriptionOptions({
  className,
  planType,
  align = 'center',
  hideFreePlan = false,
}: Props) {
  const subscriptions = trpc.subscription.getSubscriptionInfo.useQuery(
    undefined,
    {
      refetchInterval: 15000,
    },
  );

  const individualBillingInterval =
    subscriptions.data?.individual.billingInterval ?? null;
  const individualTier: SubscriptionTier =
    subscriptions.data?.individual.tier ?? 'free';
  const isIndividualActive = individualTier !== 'free';
  const activeTeamSubscription = subscriptions.data?.teams.find(
    (team) => team.subscription?.isActive,
  );
  const teamTier: SubscriptionTier =
    activeTeamSubscription?.subscription?.tier ?? 'free';
  const isTeamActive = teamTier !== 'free';
  const teamBillingInterval =
    activeTeamSubscription?.subscription?.billingInterval ?? null;

  const relevantBillingInterval =
    planType === 'team' ? teamBillingInterval : individualBillingInterval;
  // Yearly is only offered to users already on a yearly plan (we don't support
  // downgrading to monthly). Everyone else is monthly-only and the yearly toggle
  // is hidden, so isAnnual is derived purely from the current billing interval.
  const isAnnual = relevantBillingInterval === 'year';

  const isTeamAnnual =
    isAnnual ||
    (planType === 'team' &&
      isIndividualActive &&
      individualBillingInterval === 'year');

  if (planType === 'team') {
    return (
      <div
        className={cn(
          'flex gap-4',
          align === 'center' && 'justify-center',
          className,
        )}
      >
        {!hideFreePlan && <FreeTeamCard />}
        <TeamPlusCard
          isAnnual={isTeamAnnual}
          billingInterval={teamBillingInterval}
          individualBillingInterval={individualBillingInterval}
          currentTier={teamTier}
        />
        <TeamProCard
          isAnnual={isTeamAnnual}
          billingInterval={teamBillingInterval}
          individualBillingInterval={individualBillingInterval}
          currentTier={teamTier}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex gap-4',
        align === 'center' && 'justify-center',
        className,
      )}
    >
      {!hideFreePlan && <FreeCard isCurrentPlan />}
      <IndividualTierCard
        config={{
          ...plusCardConfig,
          features: plusFeatures(
            subscriptions.data?.settings.maxCreditsPerGeneration ?? 7,
          ),
        }}
        isIndividualActive={isIndividualActive}
        isTeamActive={isTeamActive}
        teamTier={teamTier}
        isAnnual={isAnnual}
        billingInterval={individualBillingInterval ?? null}
        currentTier={individualTier}
      />
      <IndividualTierCard
        config={{
          ...proCardConfig,
          features: proFeatures(
            subscriptions.data?.settings.maxCreditsPerGeneration ?? 7,
          ),
        }}
        isIndividualActive={isIndividualActive}
        isTeamActive={isTeamActive}
        teamTier={teamTier}
        isAnnual={isAnnual}
        billingInterval={individualBillingInterval ?? null}
        currentTier={individualTier}
      />
    </div>
  );
}

const FreeCard = ({isCurrentPlan}: {isCurrentPlan: boolean}) => {
  const {subscriptions} = useSubscription();
  if (!subscriptions) {
    return null;
  }

  return (
    <PricingCard
      isCurrentPlan={isCurrentPlan}
      title='Free'
      planIcon='free'
      price='$0'
      period='/month'
      features={freeFeatures(subscriptions.settings)}
      buttonText='Current plan'
    />
  );
};

// --- Individual Tier Card (shared by Plus and Pro) ---

type CheckoutPlan =
  | 'plus'
  | 'plus-annual'
  | 'pro-unlimited'
  | 'pro-unlimited-annual';

interface IndividualTierCardConfig {
  tier: SubscriptionTier;
  title: string;
  planIcon: 'plus' | 'pro';
  price: string;
  features: PlanFeature[];
  monthlyPlan: CheckoutPlan;
  annualPlan: CheckoutPlan;
  annualDialogPlanType: 'plus' | 'pro';
  amplitudeEvent: string;
  /** Button text when user has an active sub on a different tier */
  activeButtonText: string;
  annualToggle: {
    monthlyPrice: string;
    yearlyPrice: string;
    yearlyMonthlyEquivalent: string;
    discountPercent: number;
  };
}

const plusCardConfig: Omit<IndividualTierCardConfig, 'features'> = {
  tier: 'plus',
  title: 'Plus',
  planIcon: 'plus',
  price: `$${pricing.individual.plus.monthly}`,
  monthlyPlan: 'plus',
  annualPlan: 'plus-annual',
  annualDialogPlanType: 'plus',
  amplitudeEvent: '[Paywall] plus plan click',
  activeButtonText: 'Upgrade',
  annualToggle: {
    monthlyPrice: `$${pricing.individual.plus.monthly}`,
    yearlyPrice: `$${pricing.individual.plus.yearly}`,
    yearlyMonthlyEquivalent: `$${yearlyMonthlyEquivalent(pricing.individual.plus.yearly)}`,
    discountPercent,
  },
};

const proCardConfig: Omit<IndividualTierCardConfig, 'features'> = {
  tier: 'pro',
  title: 'Pro',
  planIcon: 'pro',
  price: `$${pricing.individual.pro.monthly}`,
  monthlyPlan: 'pro-unlimited',
  annualPlan: 'pro-unlimited-annual',
  annualDialogPlanType: 'pro',
  amplitudeEvent: '[Paywall] pro unlimited plan click',
  activeButtonText: 'Upgrade',
  annualToggle: {
    monthlyPrice: `$${pricing.individual.pro.monthly}`,
    yearlyPrice: `$${pricing.individual.pro.yearly}`,
    yearlyMonthlyEquivalent: `$${yearlyMonthlyEquivalent(pricing.individual.pro.yearly)}`,
    discountPercent,
  },
};

interface IndividualTierCardProps {
  config: IndividualTierCardConfig;
  isIndividualActive: boolean | undefined;
  isTeamActive: boolean | undefined;
  teamTier: SubscriptionTier;
  isAnnual: boolean;
  billingInterval: BillingInterval;
  currentTier: SubscriptionTier;
}

const IndividualTierCard = ({
  config,
  isIndividualActive,
  isTeamActive,
  teamTier,
  isAnnual,
  billingInterval,
  currentTier,
}: IndividualTierCardProps) => {
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const {data: billingDetails} = trpc.subscription.getBillingDetails.useQuery();
  const createCheckoutSessionMutation = useCheckoutSession();
  const [isAnnualConfirmOpen, setIsAnnualConfirmOpen] = useState(false);
  const [isTierUpgradeConfirmOpen, setIsTierUpgradeConfirmOpen] =
    useState(false);

  const upgradeToAnnual = trpc.subscription.upgradeToAnnual.useMutation({
    onSuccess: () => {
      void trpcUtils.subscription.getSubscriptionInfo.invalidate();
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const upgradeToPro = trpc.subscription.upgradeToPro.useMutation({
    onSuccess: () => {
      void trpcUtils.subscription.getSubscriptionInfo.invalidate();
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const isOnYearly = billingInterval === 'year';
  const isOnMonthly = billingInterval === 'month';
  const isOnTier = currentTier === config.tier;

  const canUpgradeToYearly = isOnTier && isOnMonthly && isAnnual;
  const isCurrentPlan =
    isOnTier && ((isOnYearly && isAnnual) || (isOnMonthly && !isAnnual));

  // Plus → Pro tier upgrade (issue #1)
  const canUpgradeTier =
    isIndividualActive &&
    !isTeamActive &&
    currentTier === 'plus' &&
    config.tier === 'pro';

  // Pro user viewing Plus card — disable to prevent downgrade (issue #4)
  const isLowerTier =
    isIndividualActive && currentTier === 'pro' && config.tier === 'plus';

  // Team-aware tier checks for individual tab
  const isOnTeamTier = isTeamActive && teamTier === config.tier;
  const isLowerThanTeamTier =
    isTeamActive && teamTier === 'pro' && config.tier === 'plus';

  const isDisabled = isLowerTier || isLowerThanTeamTier || isOnTeamTier;

  const handleCreate = () => {
    amplitudeService.logEvent(config.amplitudeEvent, {
      billingCycle: isAnnual ? 'yearly' : 'monthly',
    });

    if (canUpgradeToYearly) {
      setIsAnnualConfirmOpen(true);
    } else if (canUpgradeTier) {
      setIsTierUpgradeConfirmOpen(true);
    } else {
      createCheckoutSessionMutation.mutate({
        plan: isAnnual ? config.annualPlan : config.monthlyPlan,
      });
    }
  };

  const getButtonText = () => {
    if (canUpgradeToYearly) return 'Upgrade';
    if (isCurrentPlan) return 'Current plan';
    if (isLowerTier) return 'Current plan';
    if (isOnTeamTier) return 'Current plan';
    if (isTeamActive) return config.activeButtonText;
    if (isIndividualActive) return config.activeButtonText;
    return 'Continue';
  };

  if (isLowerTier || isLowerThanTeamTier) {
    return null;
  }

  return (
    <>
      <PricingCard
        title={config.title}
        planIcon={config.planIcon}
        price={config.price}
        period='/month'
        features={config.features}
        buttonText={getButtonText()}
        buttonAction={handleCreate}
        isCurrentPlan={isCurrentPlan || !!isOnTeamTier}
        disabled={isDisabled}
        isLoading={
          createCheckoutSessionMutation.isPending ||
          upgradeToAnnual.isPending ||
          upgradeToPro.isPending
        }
        annualToggle={
          // Only surface the yearly toggle to users already on a yearly plan
          // (locked on, purely to show the correct yearly pricing). Monthly and
          // free users are monthly-only, so the toggle is hidden entirely.
          isOnYearly
            ? {
                isAnnual,
                onToggle: () => {},
                ...config.annualToggle,
                disabled: true,
              }
            : undefined
        }
      />

      <AnnualUpgradeConfirmDialog
        open={isAnnualConfirmOpen}
        onOpenChange={setIsAnnualConfirmOpen}
        onConfirm={() => {
          void upgradeToAnnual
            .mutateAsync()
            .then(() => {
              setIsAnnualConfirmOpen(false);
              void navigate({to: '/', replace: true});
            })
            .catch(() => {});
        }}
        isLoading={upgradeToAnnual.isPending}
        planType={config.annualDialogPlanType}
        currentPriceLabel={
          billingDetails
            ? formatPriceLabel(
                billingDetails.priceInCents,
                billingDetails.billingInterval,
              )
            : undefined
        }
      />

      <TierUpgradeConfirmDialog
        open={isTierUpgradeConfirmOpen}
        onOpenChange={setIsTierUpgradeConfirmOpen}
        onConfirm={() => {
          if (upgradeToPro.isPending) return;
          void upgradeToPro
            .mutateAsync({isAnnual: isAnnual || isOnYearly})
            .then(() => {
              setIsTierUpgradeConfirmOpen(false);
              void navigate({to: '/', replace: true});
            })
            .catch(() => {});
        }}
        isLoading={upgradeToPro.isPending}
        isAnnual={isAnnual || isOnYearly}
        currentPriceLabel={
          billingDetails
            ? formatPriceLabel(
                billingDetails.priceInCents,
                billingDetails.billingInterval,
              )
            : undefined
        }
      />
    </>
  );
};
